/**
 * Sentry / GlitchTip event scrubber — shared by EVERY service that calls
 * `Sentry.init`.
 *
 * Why this exists: Hasura-triggered webhook events were stored in GlitchTip with
 * the FULL inbound request headers, including a live `x-hasura-admin-secret`
 * value in plain text. Anyone with monitoring access effectively held the admin
 * secret. `beforeSend` is the last point inside our own process where that can
 * be stopped, so it is stopped here, in one implementation, for everyone.
 *
 * What it redacts (values replaced with `[redacted]`, keys KEPT so it is
 * obvious the scrubber ran):
 *   - request headers, cookies, query string, body and URL query params
 *   - `extra`, `contexts`, `tags` and breadcrumb `data`
 *   - any key matching {@link SECRET_KEY_PATTERN} or {@link ALWAYS_REDACT_KEYS},
 *     matched case-insensitively
 *   - any literal occurrence of a secret this process holds in its own env
 *     (belt-and-braces: catches a secret pasted into a log line or error message)
 *
 * ⛔ CANONICAL SOURCE: `taskyinn-backend/shared/sentry/sentry-scrub.ts`.
 * Every other copy of this file is byte-identical and produced by
 * `node scripts/sync-sentry-scrub.mjs`; `scripts/checks/sentry-scrub-drift.mjs`
 * fails the build if any copy drifts. Never hand-edit a copy — the repo has
 * been bitten by duplicated gates drifting apart. This file deliberately has
 * ZERO imports so it can be dropped into any service, ESM or CJS-compiled.
 */

/** Marker written in place of a secret value. Never delete the key — a missing key looks like the scrubber never ran. */
export const REDACTED = '[redacted]'

/** Keys whose VALUE is always a secret, whatever the surrounding shape. Compared lower-cased. */
export const ALWAYS_REDACT_KEYS: readonly string[] = [
  'x-hasura-admin-secret',
  'x-hasura-access-key',
  'authorization',
  'proxy-authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-webhook-token',
  'x-channex-internal-key',
  'x-signature',
  'x-hub-signature',
  'x-hub-signature-256',
]

/**
 * Substring pattern for secret-ish key names. Case-insensitive by construction.
 *
 * ⛔ The card terms (`card_number`, `cvv`/`cvc`, `pan`, `service_code`) are
 * deliberately NARROW, not the bare word `card`: `card_brand` and
 * `card_last_four` are safe display metadata stored on `payment_transactions`
 * and are the fields you actually need when debugging a payment, so they must
 * survive. `pan` and `cv[vc]` are anchored on a non-letter boundary so
 * `expand`, `company`, `japan` and friends are not swept up — never relax those
 * anchors to a plain substring.
 */
export const SECRET_KEY_PATTERN =
  /secret|token|password|passwd|api[-_]?key|private[-_]?key|credential|signature|bearer|card[-_]?number|(?:^|[^a-z])cv[vc]|(?:^|[^a-z])pan(?:[^a-z]|$)|service[-_]?code/i

const MAX_DEPTH = 8

/** True when a key name means "the value is a secret". Case-insensitive. */
export function isSecretKey(key: string): boolean {
  const lower = String(key).toLowerCase()
  return ALWAYS_REDACT_KEYS.indexOf(lower) !== -1 || SECRET_KEY_PATTERN.test(lower)
}

let envSecretsCache: string[] | null = null

/**
 * Secret VALUES this process holds, harvested from its own environment. Used to
 * catch a secret that reached the event through a path with no telltale key —
 * an error message, a serialized curl command, a log line.
 */
export function knownEnvSecrets(): string[] {
  if (envSecretsCache) return envSecretsCache
  const found: string[] = []
  if (typeof process !== 'undefined' && process && process.env) {
    for (const key of Object.keys(process.env)) {
      const value = process.env[key]
      // Short values produce catastrophic false positives (a 4-char "secret"
      // would redact half the event), so only substantial ones are matched.
      if (isSecretKey(key) && typeof value === 'string' && value.length >= 8) found.push(value)
    }
  }
  envSecretsCache = found
  return found
}

/** Test seam: forget the harvested env secrets so the next call re-reads `process.env`. */
export function resetKnownEnvSecrets(): void {
  envSecretsCache = null
}

/** Replace every literal occurrence of a known env secret inside a string. */
export function redactKnownSecrets(text: string): string {
  let out = text
  for (const secret of knownEnvSecrets()) {
    if (out.indexOf(secret) !== -1) out = out.split(secret).join(REDACTED)
  }
  return out
}

/** Redact secret-keyed params inside a `a=1&token=xyz` query string. */
export function scrubQueryString(query: string): string {
  return query
    .split('&')
    .map((pair) => {
      const eq = pair.indexOf('=')
      if (eq === -1) return redactKnownSecrets(pair)
      const key = pair.slice(0, eq)
      const rawKey = decodeURIComponent(key.replace(/\+/g, ' '))
      if (isSecretKey(rawKey)) return key + '=' + REDACTED
      return key + '=' + redactKnownSecrets(pair.slice(eq + 1))
    })
    .join('&')
}

/** Redact secret-keyed params in a URL while leaving path and host readable. */
export function scrubUrl(url: string): string {
  const q = url.indexOf('?')
  if (q === -1) return redactKnownSecrets(url)
  const hash = url.indexOf('#', q)
  const query = hash === -1 ? url.slice(q + 1) : url.slice(q + 1, hash)
  const tail = hash === -1 ? '' : url.slice(hash)
  return redactKnownSecrets(url.slice(0, q)) + '?' + scrubQueryString(query) + tail
}

/**
 * Recursively scrub one value. `key` is the name the value arrived under — a
 * secret-ish key redacts the whole subtree, whatever its type.
 */
export function scrubValue(key: string, value: unknown, depth = 0): unknown {
  if (isSecretKey(key)) return REDACTED
  if (value == null || depth >= MAX_DEPTH) return value
  if (typeof value === 'string') return redactKnownSecrets(value)
  if (typeof value !== 'object') return value
  if (Array.isArray(value)) return value.map((item) => scrubValue(key, item, depth + 1))
  const source = value as Record<string, unknown>
  const out: Record<string, unknown> = {}
  for (const childKey of Object.keys(source)) {
    out[childKey] = scrubValue(childKey, source[childKey], depth + 1)
  }
  return out
}

/** Scrub a header bag (object or array-of-pairs), matching names case-insensitively. */
function scrubHeaders(headers: unknown): unknown {
  if (!headers || typeof headers !== 'object') return headers
  if (Array.isArray(headers)) {
    return headers.map((pair) =>
      Array.isArray(pair) && pair.length === 2
        ? [pair[0], isSecretKey(String(pair[0])) ? REDACTED : scrubValue('', pair[1], 1)]
        : pair
    )
  }
  const source = headers as Record<string, unknown>
  const out: Record<string, unknown> = {}
  for (const name of Object.keys(source)) {
    out[name] = isSecretKey(name) ? REDACTED : scrubValue('', source[name], 1)
  }
  return out
}

function scrubRequest(request: Record<string, unknown>): void {
  if (request['headers']) request['headers'] = scrubHeaders(request['headers'])
  if (request['cookies'] != null) request['cookies'] = REDACTED
  if (typeof request['url'] === 'string') request['url'] = scrubUrl(request['url'] as string)
  const query = request['query_string']
  if (typeof query === 'string') request['query_string'] = scrubQueryString(query)
  else if (query != null) request['query_string'] = scrubValue('query_string_bag', query, 1)
  if (request['data'] != null) request['data'] = scrubValue('body', request['data'], 1)
}

/**
 * `beforeSend` / `beforeSendTransaction` hook: strip every secret from an event
 * before it leaves the process. Mutates and returns the event so it can be
 * passed straight to `Sentry.init({ beforeSend: scrubSentryEvent })`.
 */
export function scrubSentryEvent<T>(event: T): T {
  if (!event || typeof event !== 'object') return event
  const e = event as unknown as Record<string, unknown>

  if (e['request'] && typeof e['request'] === 'object') {
    scrubRequest(e['request'] as Record<string, unknown>)
  }
  for (const field of ['extra', 'contexts', 'tags'] as const) {
    if (e[field] != null) e[field] = scrubValue(field + '_bag', e[field], 1)
  }
  if (typeof e['message'] === 'string') e['message'] = redactKnownSecrets(e['message'] as string)

  const breadcrumbs = e['breadcrumbs']
  if (Array.isArray(breadcrumbs)) {
    for (const crumb of breadcrumbs) {
      if (!crumb || typeof crumb !== 'object') continue
      const c = crumb as Record<string, unknown>
      if (c['data'] != null) c['data'] = scrubValue('crumb', c['data'], 1)
      if (typeof c['message'] === 'string') c['message'] = redactKnownSecrets(c['message'] as string)
    }
  }

  const exception = e['exception'] as { values?: unknown } | undefined
  if (exception && Array.isArray(exception.values)) {
    for (const entry of exception.values) {
      if (!entry || typeof entry !== 'object') continue
      const v = entry as Record<string, unknown>
      if (typeof v['value'] === 'string') v['value'] = redactKnownSecrets(v['value'] as string)
    }
  }

  return event
}
